import { z } from 'zod';

import { type ReasonCode, ReasonCodeSchema } from './reason-code';

const PROBLEM_TYPE_PREFIX = 'urn:fxmate:reason:';

/**
 * Builds the RFC 9457 `type` URI of a reason code.
 *
 * A URN rather than a URL: there is no public documentation site to link to yet (Q14).
 */
export function problemTypeFor(code: ReasonCode): string {
  return `${PROBLEM_TYPE_PREFIX}${code}`;
}

/**
 * Body of every failed API call, served as `application/problem+json` (RFC 9457).
 *
 * `reasonCode` is the only field the Mini App uses to choose a text; `title` and
 * `detail` are English, for logs and developers, and are never shown (master X2).
 * Unknown keys are accepted and stripped: a field the API adds later does not make the Mini App
 * throw the whole error away, and nothing unknown (a stack, a broker message) survives the parse
 * to reach a screen (I9, I12).
 */
export const ProblemSchema = z
  .object({
    type: z.string(),
    title: z.string(),
    status: z.int().min(400).max(599),
    reasonCode: ReasonCodeSchema,
    detail: z.string().optional(),
    instance: z.string().optional(),
    /**
     * Values a reason text interpolates, e.g. `{ minutes: 3 }` for `TELEGRAM_CODE_RATE_LIMITED`.
     * Untrusted: `reasonMessage` keeps only the names the catalogue declares for the code and
     * falls back on a text without placeholders when a value is missing or unusable (C2 R6, R8).
     *
     * Values are strings or integers, as in the API's `Problem.params` (pinned `openapi.json`). A malformed `params`
     * is dropped rather than failing the parse, so the `reasonCode` still chooses the text: the
     * screen then shows that code's fallback text, not the generic one.
     *
     * Proposal, to be validated (C2 spec §5.4).
     */
    params: z
      .record(z.string(), z.union([z.string(), z.int()]))
      .optional()
      .catch(undefined),
  })
  .refine((problem) => problem.type === problemTypeFor(problem.reasonCode), {
    message: 'type must match reasonCode',
    path: ['type'],
  });

/** A parsed problem body; see {@link ProblemSchema}. */
export type Problem = z.infer<typeof ProblemSchema>;
